import clsx from 'clsx';
import React from 'react';

import IconCheckMark from '@components/Images/Rebranding/IconCheckMark';
import { useEventTracking, useStickyOfferTable } from '@hooks';
import { TrackingLocation } from '@tracking';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';

import Offer from './Offer';

interface OfferTableProps {
  benefits: {
    name: string | React.ReactElement;
    eligibility: boolean[];
    tooltip?: string;
  }[];
  isGiftLanding?: boolean;
  offers: OfferOption[];
  onOfferClick: () => void;
  setSelectedOffer?: (plan: SelectedOffer) => void;
}

const OfferTable = ({
  benefits,
  isGiftLanding,
  offers,
  onOfferClick,
  setSelectedOffer,
}: OfferTableProps) => {
  useEventTracking({ location: TrackingLocation.OfferTable });
  const { headerRef, rowRef, tableRef } = useStickyOfferTable();

  return (
    <section ref={tableRef} className="mt-24 flex flex-col">
      <div
        ref={headerRef}
        className={clsx(
          'sticky top-[57px] z-10 border-0 border-b border-solid border-grayscale-beige bg-white py-24 sm:top-64'
        )}
      >
        <div className="flex justify-center gap-32 md:justify-end">
          {offers.map((offer, index) => (
            <Offer
              key={offer.code}
              offer={offer}
              className={clsx({ 'hidden sm:flex': index === 0 && !isGiftLanding })}
              onOfferClick={onOfferClick}
              setSelectedOffer={setSelectedOffer}
            />
          ))}
        </div>
      </div>
      <div className="flex w-full flex-col overflow-y-auto">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            ref={rowRef}
            className="relative flex flex-col items-center gap-16 border-0 border-b border-solid border-grayscale-beige py-16 last:border-b-0 md:flex-row md:justify-between md:gap-0"
          >
            <div className="relative m-0 font-suisse-intl text-overline-md">{benefit.name}</div>
            <div className="flex w-full flex-row justify-around gap-32 md:w-auto md:justify-normal">
              {offers.map(({ isPro }, offerIndex) => (
                <div
                  key={offerIndex}
                  className={clsx('text-center md:w-[190px] lg:w-[270px]', {
                    'hidden sm:block': offerIndex === 0 && !isGiftLanding,
                  })}
                >
                  {benefit.eligibility[offerIndex] && (
                    <IconCheckMark
                      className={clsx('h-[18px]', {
                        'text-primary-magenta': !isPro,
                        'text-pro-gold': isPro,
                      })}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default OfferTable;
